import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Globe, 
  Linkedin, 
  Twitter, 
  Instagram, 
  Facebook, 
  Github, 
  Youtube,
  QrCode,
  Download,
  Share2,
  Edit,
  Loader,
  AlertCircle,
  FileImage,
  FileText,
  Camera,
  Star
} from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { getFirestore, doc, setDoc, getDoc } from 'firebase/firestore';

// Function to extract username from social media URL
const extractUsername = (url) => {
  if (!url) return '';
  
  try {
    // Remove trailing slashes
    const cleanUrl = url.replace(/\/$/, '');
    
    // Extract just the username part
    if (url.includes('twitter.com/') || url.includes('x.com/')) {
      return '@' + cleanUrl.split('/').pop();
    } else if (url.includes('facebook.com/')) {
      return cleanUrl.split('/').pop();
    } else if (url.includes('instagram.com/')) {
      return '@' + cleanUrl.split('/').pop();
    } else if (url.includes('linkedin.com/in/')) {
      return cleanUrl.split('/in/').pop();
    } else if (url.includes('github.com/')) {
      return cleanUrl.split('/').pop();
    } else if (url.includes('youtube.com/')) {
      return cleanUrl.split('/').pop();
    } else {
      // For other URLs, just return the last part
      return cleanUrl.split('/').pop();
    }
  } catch (error) {
    console.error('Error extracting username:', error);
    return '';
  }
};

// Create a data URL from an image source
const convertImageToDataURL = async (imgSrc) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // This is important for handling cross-origin images
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const dataUrl = canvas.toDataURL('image/png');
        resolve(dataUrl);
      } catch (err) {
        console.error('Error converting image to data URL:', err);
        reject(err);
      }
    };
    img.onerror = (err) => {
      console.error('Error loading image for conversion:', err);
      reject(err);
    };
    img.src = imgSrc;
  });
};

const IdentityViewer = ({ isPreview = false, previewData = null }) => {
  const { currentUser } = useAuth();
  const { id } = useParams();
  const [identity, setIdentity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);
  const cardRef = useRef(null);
  const db = getFirestore();

  useEffect(() => {
    // If in preview mode, use the provided data
    if (isPreview && previewData) {
      setIdentity(previewData);
      setLoading(false);
      return;
    }

    // Otherwise, fetch the identity data from the API and Firebase
    const fetchIdentity = async () => {
      try {
        // First try to get from Firebase
        if (currentUser && id) {
          const docRef = doc(db, "digitalIdentities", id);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            console.log("Identity found in Firebase:", docSnap.data());
            setIdentity(docSnap.data());
            setLoading(false);
            return;
          }
        }
        
        // If not in Firebase or no user, try the API
        const response = await api.getIdentity(id);
        const identityData = response.identity;
        setIdentity(identityData);
        
        // Store in Firebase if user is logged in
        if (currentUser && identityData) {
          try {
            await setDoc(doc(db, "digitalIdentities", id), identityData);
            console.log("Identity saved to Firebase");
          } catch (firebaseError) {
            console.error("Error saving to Firebase:", firebaseError);
          }
        }
      } catch (error) {
        console.error('Error fetching identity:', error);
        setError('Failed to load digital identity. It may have been deleted or does not exist.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchIdentity();
    }
  }, [id, isPreview, previewData, currentUser, db]);

  // Generate QR code
  const handleGenerateQR = async () => {
    try {
      const response = await api.generateQRCode(identity.id);
      setQrCode(response.qrCode);
      setShowQrModal(true);
    } catch (error) {
      console.error('Error generating QR code:', error);
      setError('Failed to generate QR code. Please try again.');
    }
  };

  // Copy share link to clipboard
  const handleCopyLink = () => {
    const baseUrl = window.location.origin;
    const shareUrl = `${baseUrl}/digital-identity/${identity.id}`;
    
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Download vCard
  const handleDownloadVCard = () => {
    if (!identity) return;
    
    // Create vCard content
    const vCardContent = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${identity.name}`,
      identity.title ? `TITLE:${identity.title}` : '',
      identity.contact.email ? `EMAIL:${identity.contact.email}` : '',
      identity.contact.phone ? `TEL:${identity.contact.phone}` : '',
      identity.contact.website ? `URL:${identity.contact.website}` : '',
      identity.contact.address ? `ADR:;;${identity.contact.address};;;` : '',
      'END:VCARD'
    ].filter(line => line !== '').join('\n');
    
    // Create download link
    const blob = new Blob([vCardContent], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${identity.name.replace(/\s+/g, '_')}.vcf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Get template details
  const getTemplateStyles = () => {
    if (!identity) return {};
    
    // Default styles
    const defaultStyles = {
      container: 'bg-white',
      header: 'bg-blue-600',
      text: 'text-gray-900',
      card: 'bg-white border border-gray-200',
      button: 'bg-blue-600 hover:bg-blue-700',
      icon: 'text-blue-600',
      cardClass: '',
      headerClass: '',
      socialClass: 'hover:bg-gray-100',
      decorations: true,
      socialColor: 'text-gray-800' // Default social media text color
    };
    
    // Apply template-specific styles
    switch (identity.template) {
      case 'professional':
        return {
          ...defaultStyles,
          container: 'bg-gradient-to-br from-gray-50 to-gray-100',
          header: `bg-gradient-to-r from-[${identity.customization.primaryColor}] to-[${adjustColorBrightness(identity.customization.primaryColor, 20)}]`,
          card: 'bg-white border-0 shadow-2xl hover:shadow-3xl transition-all duration-500',
          button: `bg-[${identity.customization.primaryColor}] hover:bg-opacity-90 transition-all duration-300 transform hover:scale-105 shadow-lg`,
          icon: `text-[${identity.customization.primaryColor}]`,
          cardClass: 'professional-card',
          headerClass: 'professional-header',
          decorations: true,
          socialColor: 'text-gray-800'
        };
      case 'creative':
        return {
          ...defaultStyles,
          container: 'bg-gradient-to-br from-purple-50 to-pink-50',
          header: `bg-gradient-to-r from-purple-700 to-pink-600`,
          card: 'bg-white bg-opacity-80 backdrop-blur-sm border border-purple-100 shadow-2xl hover:shadow-3xl transition-all duration-500',
          button: 'bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 transition-all duration-300 transform hover:scale-105 shadow-lg',
          icon: 'text-purple-600',
          cardClass: 'creative-card',
          headerClass: 'creative-header',
          decorations: true,
          socialColor: 'text-gray-800'
        };
      case 'minimal':
        return {
          ...defaultStyles,
          container: 'bg-gray-50',
          header: 'bg-white border-b border-gray-200',
          text: 'text-gray-900',
          card: 'bg-white border-0 shadow-xl hover:shadow-2xl transition-all duration-500',
          button: `bg-[${identity.customization.primaryColor}] hover:bg-opacity-90 transition-all duration-300 transform hover:scale-105 shadow-lg`,
          icon: `text-[${identity.customization.primaryColor}]`,
          cardClass: 'minimal-card',
          headerClass: 'minimal-header text-gray-900',
          socialClass: 'hover:bg-gray-50 border border-gray-100 rounded-lg hover:shadow-md transition-all duration-300',
          decorations: false,
          socialColor: 'text-gray-800'
        };
      case 'gradient':
        return {
          ...defaultStyles,
          container: 'bg-gradient-to-br from-blue-50 to-indigo-50',
          header: `bg-gradient-to-r from-blue-600 via-indigo-500 to-purple-500`,
          card: 'bg-white bg-opacity-90 backdrop-blur-sm border-0 shadow-2xl hover:shadow-3xl transition-all duration-500',
          button: 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 transform hover:scale-105 shadow-lg',
          icon: 'text-indigo-600',
          cardClass: 'gradient-card',
          headerClass: 'gradient-header',
          socialClass: 'hover:bg-blue-50 hover:shadow-sm transition-all duration-300',
          decorations: true,
          socialColor: 'text-gray-800'
        };
      case 'dark':
        return {
          container: 'bg-gray-900',
          header: 'bg-gradient-to-r from-gray-800 to-gray-900',
          text: 'text-gray-100',
          card: 'bg-gray-800 border-0 shadow-2xl hover:shadow-3xl transition-all duration-500',
          button: `bg-[${identity.customization.primaryColor || '#10b981'}] hover:bg-opacity-90 transition-all duration-300 transform hover:scale-105 shadow-lg`,
          icon: `text-[${identity.customization.primaryColor || '#10b981'}]`,
          cardClass: 'dark-card',
          headerClass: 'dark-header',
          socialClass: 'hover:bg-gray-700 text-gray-300 hover:shadow-md transition-all duration-300',
          decorations: true,
          socialColor: 'text-gray-300'
        };
      default:
        return defaultStyles;
    }
  };
  
  // Helper function to adjust color brightness
  const adjustColorBrightness = (hex, percent) => {
    if (!hex) return '#2563eb'; // Default blue if no color provided
    
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Convert to RGB
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);
    
    // Adjust brightness
    r = Math.min(255, Math.max(0, r + percent));
    g = Math.min(255, Math.max(0, g + percent));
    b = Math.min(255, Math.max(0, b + percent));
    
    // Convert back to hex
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  };

  const styles = getTemplateStyles();

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <Loader className="h-12 w-12 text-blue-600 animate-spin mb-4" />
        <p className="text-gray-600">Loading digital identity...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="bg-red-50 border border-red-200 text-red-600 p-6 rounded-lg max-w-md w-full flex items-start">
          <AlertCircle className="h-6 w-6 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <h2 className="font-bold text-lg mb-2">Error Loading Identity</h2>
            <p>{error}</p>
            <Link to="/digital-identity" className="mt-4 inline-block text-blue-600 hover:underline">
              Return to Digital Identity Hub
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!identity) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-6 rounded-lg max-w-md w-full flex items-start">
          <AlertCircle className="h-6 w-6 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <h2 className="font-bold text-lg mb-2">Identity Not Found</h2>
            <p>The digital identity you're looking for could not be found.</p>
            <Link to="/digital-identity" className="mt-4 inline-block text-blue-600 hover:underline">
              Return to Digital Identity Hub
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Export as PNG with special preparation - with direct photo embedding
  const exportAsPNG = async () => {
    if (!cardRef.current) return;
    
    setExportLoading(true);
    try {
      // Hide the Save Contact button before capturing
      const saveContactButton = cardRef.current.querySelector('.save-contact-button');
      const originalDisplay = saveContactButton ? saveContactButton.style.display : null;
      if (saveContactButton) {
        saveContactButton.style.display = 'none';
      }
      
      // Show usernames for social media in exports
      const socialLinks = cardRef.current.querySelectorAll('.social-username-display');
      const socialIcons = cardRef.current.querySelectorAll('.social-icon-display');
      
      // Show usernames, hide icons for export
      socialLinks.forEach(el => {
        el.style.display = 'inline';
      });
      socialIcons.forEach(el => {
        el.style.display = 'none';
      });
      
      // Create a deep clone of the element to avoid modifying the original
      const captureElement = cardRef.current.cloneNode(true);
      captureElement.style.position = 'absolute';
      captureElement.style.left = '-9999px';
      document.body.appendChild(captureElement);
      
      // CUSTOM APPROACH FOR PHOTO - Create a duplicate img element explicitly
      let photoAdded = false;
      try {
        // Handle the profile photo - we'll create a new element
        const profilePhotoContainer = captureElement.querySelector('.profile-photo-container');
        if (profilePhotoContainer && identity.photoUrl) {
          // Remove existing photo element if it exists
          const existingPhoto = profilePhotoContainer.querySelector('img');
          if (existingPhoto) {
            profilePhotoContainer.removeChild(existingPhoto);
          }
          
          // Create a completely new image element
          const newImg = document.createElement('img');
          newImg.src = identity.photoUrl;
          newImg.alt = identity.name;
          newImg.className = 'w-16 h-16 rounded-full object-cover border-2 border-white shadow-md profile-photo';
          newImg.style.display = 'block';
          newImg.style.width = '64px';
          newImg.style.height = '64px';
          newImg.style.borderRadius = '9999px';
          
          // Add image to container
          profilePhotoContainer.appendChild(newImg);
          photoAdded = true;
          
          // Wait for the image to load
          await new Promise((resolve) => {
            newImg.onload = resolve;
            newImg.onerror = resolve;
            // If it's already loaded, resolve immediately
            if (newImg.complete) resolve();
          });
        }
      } catch (photoError) {
        console.error('Error with custom photo approach:', photoError);
      }
      
      // Fallback approach if the custom approach didn't work
      if (!photoAdded) {
        try {
          const profilePhoto = cardRef.current.querySelector('.profile-photo');
          const clonedPhoto = captureElement.querySelector('.profile-photo');
          
          if (profilePhoto && clonedPhoto && profilePhoto.src) {
            // Convert the image to a data URL - this helps with CORS issues
            const dataUrl = await convertImageToDataURL(profilePhoto.src);
            clonedPhoto.src = dataUrl;
            
            // Add inline style to ensure visibility
            clonedPhoto.style.display = 'block';
            clonedPhoto.style.visibility = 'visible';
            clonedPhoto.style.opacity = '1';
            clonedPhoto.style.width = '64px';
            clonedPhoto.style.height = '64px';
            clonedPhoto.style.borderRadius = '9999px';
          }
        } catch (photoError) {
          console.error('Error with fallback photo approach:', photoError);
        }
      }
      
      // Wait to ensure all changes are applied
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Perform the actual capture on our cloned element
      const canvas = await html2canvas(captureElement, {
        scale: 3, // Higher quality
        backgroundColor: null,
        logging: true, // Enable logging for debugging
        useCORS: true, // Allow cross-origin images
        allowTaint: true, // Allow cross-origin images to taint the canvas
        imageTimeout: 30000, // Longer timeout for image loading
        onclone: (clonedDoc) => {
          // Last chance to modify the cloned document before rendering
          const clonedImg = clonedDoc.querySelector('.profile-photo');
          if (clonedImg) {
            clonedImg.style.display = 'block';
            clonedImg.style.visibility = 'visible';
          }
        }
      });
      
      // Clean up - remove the cloned element
      document.body.removeChild(captureElement);
      
      // Restore the original display
      if (saveContactButton) {
        saveContactButton.style.display = originalDisplay;
      }
      
      // Hide usernames, show icons again
      socialLinks.forEach(el => {
        el.style.display = 'none';
      });
      socialIcons.forEach(el => {
        el.style.display = 'inline-flex';
      });
      
      const imgData = canvas.toDataURL('image/png', 1.0); // Use maximum quality
      const link = document.createElement('a');
      link.href = imgData;
      link.download = `${identity.name.replace(/\s+/g, '_')}_digital_card.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 3000);
    } catch (error) {
      console.error('Error exporting as PNG:', error);
      setError('Failed to export as PNG. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };
  
  // Export as PDF with special preparation
  const exportAsPDF = async () => {
    if (!cardRef.current) return;
    
    setExportLoading(true);
    try {
      // Hide the Save Contact button before capturing
      const saveContactButton = cardRef.current.querySelector('.save-contact-button');
      const originalDisplay = saveContactButton ? saveContactButton.style.display : null;
      if (saveContactButton) {
        saveContactButton.style.display = 'none';
      }
      
      // Show usernames for social media in exports
      const socialLinks = cardRef.current.querySelectorAll('.social-username-display');
      const socialIcons = cardRef.current.querySelectorAll('.social-icon-display');
      
      // Show usernames, hide icons for export
      socialLinks.forEach(el => {
        el.style.display = 'inline';
      });
      socialIcons.forEach(el => {
        el.style.display = 'none';
      });
      
      // Create a deep clone of the element to avoid modifying the original
      const captureElement = cardRef.current.cloneNode(true);
      captureElement.style.position = 'absolute';
      captureElement.style.top = '-9999px';
      document.body.appendChild(captureElement);
      
      // Handle the profile photo specifically to ensure it appears in exports
      try {
        const profilePhoto = cardRef.current.querySelector('.profile-photo');
        const clonedPhoto = captureElement.querySelector('.profile-photo');
        
        if (profilePhoto && clonedPhoto && profilePhoto.src) {
          // Convert the image to a data URL - this helps with CORS issues
          const dataUrl = await convertImageToDataURL(profilePhoto.src);
          clonedPhoto.src = dataUrl;
          
          // Add inline style to ensure visibility
          clonedPhoto.style.display = 'block';
          clonedPhoto.style.visibility = 'visible';
          clonedPhoto.style.opacity = '1';
        }
      } catch (photoError) {
        console.error('Error processing profile photo:', photoError);
        // Continue with export even if photo processing fails
      }
      
      // Wait to ensure all changes are applied
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Perform the actual capture on our cloned element
      const canvas = await html2canvas(captureElement, {
        scale: 3, // Higher quality
        backgroundColor: null,
        logging: true, // Enable logging for debugging
        useCORS: true, // Allow cross-origin images
        allowTaint: true, // Allow cross-origin images to taint the canvas
        imageTimeout: 30000, // Longer timeout for image loading
        onclone: (clonedDoc) => {
          // Last chance to modify the cloned document before rendering
          const clonedImg = clonedDoc.querySelector('.profile-photo');
          if (clonedImg) {
            clonedImg.style.display = 'block';
            clonedImg.style.visibility = 'visible';
          }
        }
      });
      
      // Clean up - remove the cloned element
      document.body.removeChild(captureElement);
      
      // Restore the original display
      if (saveContactButton) {
        saveContactButton.style.display = originalDisplay;
      }
      
      // Hide usernames, show icons again
      socialLinks.forEach(el => {
        el.style.display = 'none';
      });
      socialIcons.forEach(el => {
        el.style.display = 'inline-flex';
      });
      
      const imgData = canvas.toDataURL('image/png', 1.0); // Use maximum quality
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      // Calculate dimensions to fit on A4
      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`${identity.name.replace(/\s+/g, '_')}_digital_card.pdf`);
      
      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 3000);
    } catch (error) {
      console.error('Error exporting as PDF:', error);
      setError('Failed to export as PDF. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className={`min-h-screen ${styles.container} ${styles.text}`}>
      {/* Header */}
      <div className={`${styles.header} text-white p-6 md:p-10`}>
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="mb-6 md:mb-0">
              <h1 className="text-3xl md:text-4xl font-bold animate-fadeIn">{identity.name}</h1>
              {identity.title && (
                <p className="text-xl mt-2 text-white text-opacity-90 animate-slideInRight">{identity.title}</p>
              )}
            </div>
            
            {/* Action buttons for owner */}
            {(currentUser || isPreview) && (
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowExportModal(true)}
                  className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-md flex items-center transition-all duration-200 hover:scale-105"
                >
                  <Download className="h-5 w-5 mr-2" />
                  Export
                </button>
                <button
                  onClick={() => setShowShareModal(true)}
                  className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-md flex items-center transition-all duration-200 hover:scale-105"
                >
                  <Share2 className="h-5 w-5 mr-2" />
                  Share
                </button>
                
                {!isPreview && (
                  <Link
                    to={`/digital-identity/${identity.id}/edit`}
                    className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-md flex items-center transition-all duration-200 hover:scale-105"
                  >
                    <Edit className="h-5 w-5 mr-2" />
                    Edit
                  </Link>
                )}
              </div>
            )}
          </div>
          
          {identity.about && (
            <div className="mt-6 max-w-2xl animate-fadeIn">
              <p className="text-white text-opacity-90">{identity.about}</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Digital Business Card - This is the element that will be captured for export */}
      <div className="max-w-4xl mx-auto p-6 md:p-10 mt-2">
        <div ref={cardRef} className="overflow-hidden">
          {/* Business Card */}
          <div className={`${styles.card} ${styles.cardClass} rounded-xl overflow-hidden transform transition-all duration-500 animate-fadeIn`}>
            {/* Card Header */}
            <div className={`${styles.header} ${styles.headerClass} text-white p-6 relative overflow-hidden shadow-lg`}>
              {/* Decorative elements */}
              {styles.decorations && (
                <>
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-white opacity-10 rounded-full -ml-12 -mb-12"></div>
                </>
              )}
              
              <div className="relative z-10 flex items-center animate-fadeIn">
                {(identity.photoUrl || identity.photo) && (
                  <div className="mr-4 flex-shrink-0 profile-photo-container">
                    <img 
                      src={identity.photoUrl || identity.photo} 
                      alt={identity.name} 
                      className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-md profile-photo"
                    />
                  </div>
                )}
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold animate-fadeIn">{identity.name}</h2>
                  {identity.title && (
                    <p className="text-lg mt-1 text-white text-opacity-90 animate-slideInRight">{identity.title}</p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Card Content */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 bg-gradient-to-b from-white to-gray-50">
              {/* Contact Information */}
              <div className="space-y-4 animate-fadeIn" style={{animationDelay: '0.2s'}}>
                <h3 className="text-lg font-semibold mb-3 flex items-center border-b pb-2 transition-all duration-300 hover:translate-x-1">
                  <User className={`h-5 w-5 mr-2 ${styles.icon}`} />
                  Contact Information
                </h3>
                
                <div className="space-y-4 transform transition-all duration-500 hover:translate-x-1">
                  {identity.contact.email && (
                    <div className="flex items-start group contact-info-item">
                      <Mail className={`h-5 w-5 mr-3 mt-0.5 ${styles.icon} group-hover:scale-110 transition-transform`} />
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <a 
                          href={`mailto:${identity.contact.email}`} 
                          className="hover:underline font-medium"
                        >
                          {identity.contact.email}
                        </a>
                      </div>
                    </div>
                  )}
                  
                  {identity.contact.phone && (
                    <div className="flex items-start group contact-info-item">
                      <Phone className={`h-5 w-5 mr-3 mt-0.5 ${styles.icon} group-hover:scale-110 transition-transform`} />
                      <div>
                        <p className="text-sm text-gray-500">Phone</p>
                        <a 
                          href={`tel:${identity.contact.phone}`} 
                          className="hover:underline font-medium"
                        >
                          {identity.contact.phone}
                        </a>
                      </div>
                    </div>
                  )}
                  
                  {identity.contact.website && (
                    <div className="flex items-start group contact-info-item">
                      <Globe className={`h-5 w-5 mr-3 mt-0.5 ${styles.icon} group-hover:scale-110 transition-transform`} />
                      <div>
                        <p className="text-sm text-gray-500">Website</p>
                        <a 
                          href={identity.contact.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="hover:underline font-medium"
                        >
                          {identity.contact.website.replace(/^https?:\/\//, '')}
                        </a>
                      </div>
                    </div>
                  )}
                  
                  {identity.contact.address && (
                    <div className="flex items-start group contact-info-item">
                      <MapPin className={`h-5 w-5 mr-3 mt-0.5 ${styles.icon} group-hover:scale-110 transition-transform`} />
                      <div>
                        <p className="text-sm text-gray-500">Address</p>
                        <a 
                          href={`https://maps.google.com/?q=${encodeURIComponent(identity.contact.address)}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="hover:underline font-medium"
                        >
                          {identity.contact.address}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Social Media */}
              <div className="animate-fadeIn" style={{animationDelay: '0.4s'}}>
                <h3 className="text-lg font-semibold mb-3 flex items-center border-b pb-2">
                  <Globe className={`h-5 w-5 mr-2 ${styles.icon}`} />
                  Social Media
                </h3>
                
                <div className="grid grid-cols-2 gap-3 transform transition-all duration-500 hover:translate-x-1">
                  {identity.social.linkedin && (
                      <a 
                      href={identity.social.linkedin} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={`flex items-center p-2 rounded-md ${styles.socialClass} transition-all duration-200 hover:scale-105 social-icon`}
                    >
                      <span className="social-icon-display inline-flex items-center">
                        <Linkedin className={`h-5 w-5 mr-2 ${styles.icon}`} />
                        <span className={`text-sm font-medium ${styles.socialColor}`}>LinkedIn</span>
                      </span>
                      <span className="social-username-display hidden text-sm font-bold text-gray-800">
                        <Linkedin className={`h-5 w-5 mr-2 ${styles.icon}`} />
                        {extractUsername(identity.social.linkedin)}
                      </span>
                    </a>
                  )}
                  
                  {identity.social.twitter && (
                      <a 
                      href={identity.social.twitter} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={`flex items-center p-2 rounded-md ${styles.socialClass} transition-all duration-200 hover:scale-105 social-icon`}
                    >
                      <span className="social-icon-display inline-flex items-center">
                        <Twitter className={`h-5 w-5 mr-2 ${styles.icon}`} />
                        <span className={`text-sm font-medium ${styles.socialColor}`}>Twitter</span>
                      </span>
                      <span className="social-username-display hidden text-sm font-bold text-gray-800">
                        <Twitter className={`h-5 w-5 mr-2 ${styles.icon}`} />
                        {extractUsername(identity.social.twitter)}
                      </span>
                    </a>
                  )}
                  
                  {identity.social.instagram && (
                      <a 
                      href={identity.social.instagram} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={`flex items-center p-2 rounded-md ${styles.socialClass} transition-all duration-200 hover:scale-105 social-icon`}
                    >
                      <span className="social-icon-display inline-flex items-center">
                        <Instagram className={`h-5 w-5 mr-2 ${styles.icon}`} />
                        <span className={`text-sm font-medium ${styles.socialColor}`}>Instagram</span>
                      </span>
                      <span className="social-username-display hidden text-sm font-bold text-gray-800">
                        <Instagram className={`h-5 w-5 mr-2 ${styles.icon}`} />
                        {extractUsername(identity.social.instagram)}
                      </span>
                    </a>
                  )}
                  
                  {identity.social.facebook && (
                      <a 
                      href={identity.social.facebook} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={`flex items-center p-2 rounded-md ${styles.socialClass} transition-all duration-200 hover:scale-105 social-icon`}
                    >
                      <span className="social-icon-display inline-flex items-center">
                        <Facebook className={`h-5 w-5 mr-2 ${styles.icon}`} />
                        <span className={`text-sm font-medium ${styles.socialColor}`}>Facebook</span>
                      </span>
                      <span className="social-username-display hidden text-sm font-bold text-gray-800">
                        <Facebook className={`h-5 w-5 mr-2 ${styles.icon}`} />
                        {extractUsername(identity.social.facebook)}
                      </span>
                    </a>
                  )}
                  
                  {identity.social.github && (
                      <a 
                      href={identity.social.github} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={`flex items-center p-2 rounded-md ${styles.socialClass} transition-all duration-200 hover:scale-105 social-icon`}
                    >
                      <span className="social-icon-display inline-flex items-center">
                        <Github className={`h-5 w-5 mr-2 ${styles.icon}`} />
                        <span className={`text-sm font-medium ${styles.socialColor}`}>GitHub</span>
                      </span>
                      <span className="social-username-display hidden text-sm font-bold text-gray-800">
                        <Github className={`h-5 w-5 mr-2 ${styles.icon}`} />
                        {extractUsername(identity.social.github)}
                      </span>
                    </a>
                  )}
                  
                  {identity.social.youtube && (
                      <a 
                      href={identity.social.youtube} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={`flex items-center p-2 rounded-md ${styles.socialClass} transition-all duration-200 hover:scale-105 social-icon`}
                    >
                      <span className="social-icon-display inline-flex items-center">
                        <Youtube className={`h-5 w-5 mr-2 ${styles.icon}`} />
                        <span className={`text-sm font-medium ${styles.socialColor}`}>YouTube</span>
                      </span>
                      <span className="social-username-display hidden text-sm font-bold text-gray-800">
                        <Youtube className={`h-5 w-5 mr-2 ${styles.icon}`} />
                        {extractUsername(identity.social.youtube)}
                      </span>
                    </a>
                  )}
                </div>
                
                {Object.values(identity.social).every(val => !val) && (
                  <p className="text-gray-500 text-center py-4 text-sm">No social media profiles added</p>
                )}
              </div>
            </div>
            
            {/* Card Footer */}
            <div className="px-6 pb-6 pt-2 text-center">
              <div className="flex justify-center space-x-4">
                <button
                  onClick={handleDownloadVCard}
                  className={`${styles.button} text-white px-5 py-2.5 rounded-full flex items-center text-sm hover:scale-105 transition-transform shadow-md save-contact-button`}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Save Contact
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="mt-10 text-center">
          <p className="text-sm text-gray-500">
            Created with <a href="https://tools.kangleicode.in" className="text-blue-600 hover:underline">KangleiCode Tools</a>
          </p>
        </div>
      </div>
      
      {/* QR Code Modal */}
      {showQrModal && qrCode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4 text-gray-900">Share Your Digital Identity</h2>
            <p className="text-gray-600 mb-4">
              Share your digital identity with others using this QR code.
            </p>
            
            <div className="flex justify-center mb-4">
              <img src={qrCode} alt="QR Code" className="w-64 h-64 rounded-lg shadow-xl border-4 border-white" />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowQrModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
              <a
                href={qrCode}
                download="digital-identity-qr.png"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Download QR Code
              </a>
            </div>
          </div>
        </div>
      )}
      
      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4 text-gray-900">Share Your Digital Identity</h2>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Share Link
              </label>
              <div className="flex">
                <input
                  type="text"
                  readOnly
                  value={`${window.location.origin}/digital-identity/${identity.id}`}
                  className="flex-grow p-2 border border-gray-300 rounded-l-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  onClick={handleCopyLink}
                  className="px-4 py-2 bg-gray-100 border border-gray-300 border-l-0 rounded-r-md hover:bg-gray-200"
                >
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
            
            <div className="mb-6">
              <button
                onClick={handleGenerateQR}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center justify-center"
              >
                <QrCode className="h-5 w-5 mr-2" />
                Generate QR Code
              </button>
            </div>
            
            <div className="flex justify-end">
              <button
                onClick={() => setShowShareModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4 text-gray-900">Export Digital Business Card</h2>
            <p className="text-gray-600 mb-6">
              Choose a format to export your digital business card.
            </p>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <button
                onClick={exportAsPNG}
                disabled={exportLoading}
                className="flex flex-col items-center justify-center p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 group"
              >
                <FileImage className="h-12 w-12 text-blue-600 mb-3 group-hover:scale-110 transition-transform animate-pulse" />
                <span className="font-medium">PNG Image</span>
                <span className="text-xs text-gray-500 mt-1">High quality image</span>
              </button>
              
              <button
                onClick={exportAsPDF}
                disabled={exportLoading}
                className="flex flex-col items-center justify-center p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 group"
              >
                <FileText className="h-12 w-12 text-red-600 mb-3 group-hover:scale-110 transition-transform animate-pulse" />
                <span className="font-medium">PDF Document</span>
                <span className="text-xs text-gray-500 mt-1">Print-ready format</span>
              </button>
            </div>
            
            {exportLoading && (
              <div className="flex items-center justify-center py-4">
                <Loader className="h-8 w-8 text-blue-600 animate-spin mr-3" />
                <p>Generating export...</p>
              </div>
            )}
            
            {exportSuccess && (
              <div className="bg-green-50 text-green-700 p-4 rounded-md mb-6 flex items-center">
                <Star className="h-5 w-5 mr-2" />
                <p>Export successful! File downloaded.</p>
              </div>
            )}
            
            <div className="flex justify-end">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IdentityViewer;
